import { useState, useEffect, useRef } from "react";
import { nanoid } from "nanoid";
import { authClient } from "../../lib/auth-client";
import axios from "axios";
import { Avatar, Button, Spinner } from "@material-tailwind/react";
import Toast from "../../components/Toast/Toast";
import { API_URL } from "../../core/constants";
import { useNavigate } from "react-router-dom";
import bg from "../../assets/well_small.jpg";
import Logo from "../../components/Logo/Logo";

const MFARespond = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [incorrectPin, setIncorrectPin] = useState(false);
  const [alreadyRequested, setAlreadyRequested] = useState(false);

  const {
    data: session,
    isPending, //loading state
  } = authClient.useSession();

  const [pin1, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [pin3, setPin3] = useState("");
  const [pin4, setPin4] = useState("");
  const [pin5, setPin5] = useState("");
  const [pin6, setPin6] = useState("");
  const [fullPin, setFullPin] = useState("");

  const inputRefs = useRef([]);

  useEffect(() => {
    const requestMFA = async () => {
      setLoading(true);
      setIncorrectPin(false);
      setAlreadyRequested(false);

      axios
        .post(
          `${API_URL}/jupiter/mfa/challenge`,
          {},
          {
            withCredentials: true,
            validateStatus: () => true, // Treat all status codes as valid
          }
        )
        .then((response) => {
          if (response.status === 400) {
            setAlreadyRequested(true);
            setIncorrectPin(false);
          }

          if (response.status === 401) {
            setAlreadyRequested(false);
            setIncorrectPin(true);
          }

          if (response.status === 500) {
            setAlreadyRequested(false);
            setIncorrectPin(false);
            Toast.popError("Something has gone wrong. Please try again.");
          }
        })
        .catch((error) => {
          console.error("Error during MFA request:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (session && !isPending) {
      requestMFA();
    }
  }, [session, isPending]);

  useEffect(() => {
    // Combine all pin inputs into a single string
    setFullPin(`${pin1}${pin2}${pin3}${pin4}${pin5}${pin6}`);
  }, [pin1, pin2, pin3, pin4, pin5, pin6]);

  const handleInputChange = (e, setPin, index) => {
    const value = e.target.value;
    if (/^\d?$/.test(value)) {
      setPin(value); // Only allow numeric input
      if (value && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus(); // Auto-tab to the next input
      } else if (value && index === 5) {
        // If the last input is filled, trigger handleSubmit
        handleSubmit();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (
      e.key === "Backspace" &&
      !e.target.value &&
      inputRefs.current[index - 1]
    ) {
      inputRefs.current[index - 1].focus(); // Auto-tab to the previous input on backspace
    }
  };

  const handleSubmit = (e) => {
    const currentFullPin = inputRefs.current
      .map((input) => input?.value || "")
      .join("");

    console.log("MFA code:", fullPin);
    console.log("Submitting MFA code:", currentFullPin);

    setLoading(true);
    setIncorrectPin(false);

    try {
      axios
        .post(
          `${API_URL}/jupiter/mfa/respond/`,
          {
            mfaCode: currentFullPin,
          },
          { withCredentials: true, validateStatus: () => true }
        )
        .then(function (response) {
          console.log("response", response);

          // respond to given mfa response
          if (response.status === 401) {
            setLoading(false);
            setPin1("");
            setPin2("");
            setPin3("");
            setPin4("");
            setPin5("");
            setPin6("");
            setFullPin("");
            setIncorrectPin(true);
            return false;
          }

          if (response.status === 200) {
            //only redirect if all is juicy
            navigate(`/jupiter/shared-secret/`, { replace: true });
          }
        })
        .catch(function (error) {
          Toast.popError("Something has gone wrong. Please try again.");
          console.log(error);
          setLoading(false);
        });
    } catch (error) {
      console.error("Error during MFA submission:", error);
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    const cacheBuster = nanoid();
    await authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          navigate("/jupiter/?c=" + cacheBuster, { replace: true });
        },
      },
    });
  };

  const renderLoading = () => {
    return (
      <div className="flex-1">
        <h2 className="text-4xl font-bold text-center text-gray-700">
          <Logo isDark />
        </h2>
        <div className="flex justify-center items-center flex-col mt-8">
          <Spinner className="h-12 w-12" />
          <span>One moment...</span>
        </div>
      </div>
    );
  };

  const renderIncorrectPin = () => {
    return (
      <div className="flex justify-center items-center flex-col mt-8 bg-red-100 p-4 rounded-md">
        <span>Incorrect PIN. Please try again.</span>
      </div>
    );
  };

  const renderAlreadyRequested = () => {
    return (
      <div className="flex justify-center items-center flex-col mt-8 bg-red-100 p-4 rounded-md">
        <span>
          Already requested. Please wait for your email to arrive. It is valid
          for 10 minutes.
        </span>
      </div>
    );
  };

  return (
    <div className="bg-white">
      <div className="flex justify-center h-screen">
        <div
          className="hidden bg-cover lg:block lg:w-1/2"
          style={{
            backgroundImage: `url(${bg})`,
            backgroundPosition: "40% 50%",
          }}
        ></div>

        <div className="flex items-center w-full max-w-md px-6 mx-auto lg:w-1/2 lg:max-w-1/2">
          {loading ? (
            renderLoading()
          ) : (
            <div className="flex-1">
              <div className="text-center">
                <h2 className="text-4xl font-bold text-center text-gray-700">
                  <Logo isDark />
                </h2>

                <div className="mt-8">
                  <Avatar
                    src={
                      session?.user.image ||
                      "https://zeropa.s3.eu-west-1.amazonaws.com/blank.jpg"
                    }
                    referrerPolicy="no-referrer"
                    alt={`${session?.user.name}'s avatar`}
                    size="lg"
                  />
                  <h2 className="mt-2 text-lg">
                    Welcome back {session?.user.name}
                  </h2>

                  <p className="mt-3 text-gray-500">
                    We have sent a verification code to your registered email
                    address{" "}
                    <span className="text-purple-600">
                      {session?.user.email}
                    </span>
                    . Please enter the code below to continue.
                  </p>

                  {incorrectPin && renderIncorrectPin()}
                  {alreadyRequested && renderAlreadyRequested()}
                </div>
              </div>

              <div className="mt-8 mb-8">
                <div
                  className="flex gap-x-3 bg-gray-100 px-2 py-4 rounded-md justify-center"
                  data-hs-pin-input=""
                >
                  {[setPin1, setPin2, setPin3, setPin4, setPin5, setPin6].map(
                    (setPin, index) => (
                      <input
                        key={index}
                        type="number"
                        maxLength="1"
                        ref={(el) => (inputRefs.current[index] = el)}
                        onChange={(e) => handleInputChange(e, setPin, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        value={
                          [pin1, pin2, pin3, pin4, pin5, pin6][index] // Dynamically bind value
                        }
                        className="block w-12 h-12 text-center border-gray-200 rounded-md text-lg [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                        placeholder="⚬"
                        data-hs-pin-input-item=""
                        autoComplete="false"
                        data-1p-ignore
                      />
                    )
                  )}
                </div>
              </div>

              <div className="flex flex-col w-full">
                <Button onClick={handleSubmit}>Continue</Button>
              </div>

              <div className="mt-4">
                <button
                  className="text-sm hover:cursor-pointer text-blue-600 hover:text-blue-800"
                  onClick={handleSignOut}
                >
                  Go back to login
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MFARespond;
